/**
 * TreasuryCategoryManagement — Admin
 * Gestion des catégories de trésorerie (recettes / dépenses).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Wallet, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import {
  getTreasuryCategories,
  createTreasuryCategory,
  updateTreasuryCategory,
  deleteTreasuryCategory,
} from '../../api';
import { useStructure } from '../../contexts/StructureContext';

const TYPE_OPTIONS = [
  { value: 'income', label: 'Recettes' },
  { value: 'expense', label: 'Dépenses' },
];

const EMPTY_FORM = {
  name: '',
  type: 'income',
  color: '#10b981',
  icon: '',
  order: '',
  parentId: '',
  structureId: '',
};

const TypeBadge = ({ type }) => {
  if (type === 'income') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Recettes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      Dépenses
    </span>
  );
};

const TreasuryCategoryManagement = () => {
  const { structures } = useStructure();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStructureId, setFilterStructureId] = useState('');

  // Modal state — null = closed, 'add' = add, 'edit' = edit
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterStructureId ? { structureId: filterStructureId } : {};
      const response = await getTreasuryCategories(params);
      setCategories(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStructureId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const rootCategories = categories.filter((c) => !c.parentId);

  const resetModal = () => {
    setModalMode(null);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaving(false);
    setError(null);
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, structureId: filterStructureId || '' });
    setEditingId(null);
    setModalMode('add');
    setError(null);
  };

  const openEdit = (cat) => {
    setForm({
      name: cat.name || '',
      type: cat.type || 'income',
      color: cat.color || '#10b981',
      icon: cat.icon || '',
      order: cat.order !== null && cat.order !== undefined ? String(cat.order) : '',
      parentId: cat.parentId ? String(cat.parentId) : '',
      structureId: cat.structureId ? String(cat.structureId) : '',
    });
    setEditingId(cat.id);
    setModalMode('edit');
    setError(null);
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    type: form.type,
    color: form.color || null,
    icon: form.icon.trim() || null,
    order: form.order !== '' ? parseInt(form.order, 10) : 0,
    parentId: form.parentId ? parseInt(form.parentId, 10) : null,
    structureId: form.structureId || null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      setError(null);
      if (modalMode === 'add') {
        await createTreasuryCategory(buildPayload());
      } else {
        await updateTreasuryCategory(editingId, buildPayload());
      }
      resetModal();
      fetchCategories();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Ce nom existe déjà pour cette structure et ce type');
      } else {
        setError(err.response?.data?.error || (modalMode === 'add' ? "Erreur lors de l'ajout" : 'Erreur lors de la modification'));
        console.error(err);
      }
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    const hasChildren = categories.some((c) => c.parentId === cat.id);
    if (hasChildren) {
      setError(`Impossible de supprimer "${cat.name}" : des sous-catégories y sont rattachées. Supprimez-les d'abord.`);
      return;
    }
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteTreasuryCategory(cat.id);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  const inputCls = 'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm w-full';

  if (loading && categories.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent>
            <p className="text-gray-500 py-8 text-center">Chargement...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet size={20} />
            Catégories de trésorerie
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            Les catégories partagées (sans structure) s'appliquent à toutes les structures.
            Les catégories propres à une structure ne sont visibles que dans celle-ci.
          </p>

          {/* Structure filter */}
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-gray-700 flex-shrink-0">Structure :</label>
            <select
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              value={filterStructureId}
              onChange={(e) => setFilterStructureId(e.target.value)}
            >
              <option value="">Toutes (partagées + structure)</option>
              {structures.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {/* Table */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-3">
              Catégories ({categories.length})
            </h3>
            {categories.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucune catégorie. Ajoutez-en une ci-dessous.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-sm font-semibold text-gray-700">Nom</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Type</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Couleur</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Ordre</th>
                      <th className="text-center px-3 py-2 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((cat) => {
                      const parent = cat.parentId ? categories.find((c) => c.id === cat.parentId) : null;
                      return (
                        <tr key={cat.id} className="border-t hover:bg-gray-50 transition">
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="text-gray-800 font-medium">
                                {cat.parentId && (
                                  <span className="text-gray-400 mr-1">↳</span>
                                )}
                                {cat.name}
                              </span>
                              {parent && (
                                <span className="text-xs text-gray-400">sous {parent.name}</span>
                              )}
                              {!cat.structureId && (
                                <span className="text-xs text-primary mt-0.5">Partagée</span>
                              )}
                              {cat.structureId && (
                                <span className="text-xs text-gray-400 mt-0.5">
                                  {structures.find((s) => s.id === cat.structureId)?.name || `Structure #${cat.structureId}`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-center px-3 py-2">
                            <TypeBadge type={cat.type} />
                          </td>
                          <td className="text-center px-3 py-2">
                            <span
                              style={{ background: cat.color || '#6b7280' }}
                              className="w-4 h-4 rounded-full inline-block border border-gray-200"
                              title={cat.color || 'Aucune couleur'}
                            />
                          </td>
                          <td className="text-center px-3 py-2 text-sm text-gray-600">
                            {cat.order !== null && cat.order !== undefined ? cat.order : '—'}
                          </td>
                          <td className="text-center px-3 py-2">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => openEdit(cat)}
                                className="p-1 text-primary hover:bg-primary/5 rounded-full transition"
                                title="Modifier"
                                disabled={loading}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(cat)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded-full transition"
                                title="Supprimer"
                                disabled={loading}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="text-base font-semibold text-gray-800">
                {modalMode === 'add' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
              </h2>
              <button onClick={resetModal} className="text-gray-400 hover:text-gray-600 transition">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={inputCls}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="ex: Subventions CAF"
                  required
                  autoFocus
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  className={inputCls}
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  required
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Couleur + Icône */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0.5"
                      value={form.color || '#10b981'}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    />
                    <input
                      type="text"
                      className="flex-1 px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      value={form.color || ''}
                      onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                      placeholder="#10b981"
                      maxLength={7}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.icon}
                    onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                    placeholder="ex: TrendingUp"
                  />
                </div>
              </div>

              {/* Ordre + Catégorie parente */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordre</label>
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={form.order}
                    onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie parente</label>
                  <select
                    className={inputCls}
                    value={form.parentId}
                    onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
                  >
                    <option value="">Aucune (racine)</option>
                    {rootCategories
                      .filter((c) => c.id !== editingId)
                      .map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name} ({c.type === 'income' ? 'Recettes' : 'Dépenses'})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Structure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Structure</label>
                <select
                  className={inputCls}
                  value={form.structureId}
                  onChange={(e) => setForm((f) => ({ ...f, structureId: e.target.value }))}
                >
                  <option value="">Partagée (toutes les structures)</option>
                  {structures.map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50 text-sm"
                  disabled={saving || !form.name.trim()}
                >
                  <Check size={16} />
                  {modalMode === 'add' ? 'Créer' : 'Enregistrer'}
                </button>
                <button
                  type="button"
                  onClick={resetModal}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition text-sm"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FAB */}
      {!modalMode && (
        <button
          onClick={openAdd}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter une catégorie"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default TreasuryCategoryManagement;
